import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { MapContainer, GeoJSON, Polyline, CircleMarker, Marker, Popup, useMap, useMapEvents, ZoomControl } from "react-leaflet";
import { gpx } from "@tmcw/togeojson";
import "leaflet/dist/leaflet.css";
import { Loader2, AlertCircle, Maximize2, Minimize2, MapPin, Star, Mountain, BarChart3, Locate } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import "leaflet-geosearch/dist/geosearch.css";
// b99.10.10 (Fase 2B) — ElevationProfileWeb removido; agora consumido via
// `TripOverviewElevationPanel` (mapShell). Mantemos o componente original
// para o painel público fazer reuse; o RouteMapViewer já não o importa
// directamente.
// b99.10.07 (2026-05-19) — Fase 2A da unificação dos mapas. Os painéis
// de altitude (perfil + histograma) passam a ser os MESMOS componentes
// usados pelo TripOverviewMap público. Comportamento: painel renderiza
// dentro do mapa (flex-col com flex-1 no mapa), o mapa encolhe para
// dar espaço ao painel — em vez de empurrar o painel para baixo do
// mapa como antes (b99.10.05).
import TripOverviewElevationPanel from "./tripOverviewMap/ElevationPanel";
import TripOverviewHistogramPanel from "./tripOverviewMap/HistogramPanel";
// b99.10.10 — Fase 2B: tile providers + layer selector partilhados com
// TripOverviewMap via mapShell/. Elimina TILE_LAYERS local + 2 cópias do
// seletor de camadas inline/fullscreen.
import MapTileLayer from "./mapShell/MapTileLayer";
import LayerSelector from "./mapShell/LayerSelector";

// Fix for default marker icons in Leaflet with webpack
import L from "leaflet";
import { BACKEND_URL } from "../config";
import api, { authFetch, getSessionToken } from "../lib/api";
import { useTripSegmentElevation } from "./tripMap/useTripSegmentElevation";
import { parseGoogleMapsURLViaBackend, RouteParseError } from "../lib/routes-tools/parsers";
import DirectionArrows from "./trip/DirectionArrows";
import { MapTooltipsContext, MapTooltip, useTooltipToggle } from "./maps/MapTooltipToggle";
import { MapArrowsContext, useArrowsToggle } from "./maps/MapArrowsToggle";
import MapVisibilityMenu from "./maps/MapVisibilityMenu";

// Average riding speed (km/h) used to ESTIMATE a stage duration from its
// distance when the GPX has no <time> stamps (planned exports) — mirrors
// the backend `AVG_ROUTE_SPEED_KMH` so client and server agree.
const AVG_ROUTE_SPEED_KMH = 65;
// b99.9.39 — Single source of truth for ALL map pin icons (start /
// finish / circular / intermediate / POI). Replaces the previous
// in-file SVG definitions that drifted from EditorPage and
// TripOverviewMap. Mandatory for any new map view: never declare
// your own L.divIcon for these roles — import from here.
import {
  startIcon, finishIcon, circularIcon, segmentIcon, dotIcon, hoverIcon,
  poiIcon, ROUTE_COLOR,
  POLYLINE_OUTLINE, POLYLINE_PRIMARY,
} from "./maps/mapIcons";
import ViaShapingDots from "./maps/ViaShapingDots";
import { HoverHighlight } from "./maps/RouteShapes";
import {
  pickRouteSource, isCircularRoute, startEndFromCoords, nativeStopsFromGeometry,
} from "@/lib/routes-tools/routeRenderModel";
import { toast } from "sonner";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Returns a display-friendly name, or null if the name is just coordinates
function getDisplayName(name) {
  if (!name) return null;
  let s = name.trim();
  // b99.9.61 — URL-decode UTF-8 escape sequences ("Le%C3%A7a" → "Leça",
  // "S%2FN" → "S/N"). The Google Maps URL parser may return the name
  // exactly as embedded in the URL (percent-encoded).
  if (/%[0-9A-Fa-f]{2}/.test(s)) {
    try { s = decodeURIComponent(s); } catch (e) { /* keep original */ }
  }
  // Matches "41.551, -8.428" or "41.551058,-8.428005" etc.
  if (/^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/.test(s)) return null;
  // b99.9.34 — keep only the first comma-separated chunk so the
  // tooltip is short (e.g. "Faro de Leça Faro" instead of the
  // full "Faro de Leça Faro, R. Cel. Helder Ribeiro, 4450-686
  // Matosinhos, Portugal"). Matches the Editor de Rotas behaviour
  // — single source of label-shortening across the app.
  return s.split(",")[0].trim() || null;
}

// b99.9.39 — All marker icons (startIcon, finishIcon, circularIcon,
// segmentIcon, poiIcon, POI_CATEGORY_COLORS, ROUTE_COLOR) are imported
// from `./maps/mapIcons.js`. Previous inline SVG definitions removed
// to eliminate cross-map drift.

// ROUTE-RENDER-MODEL (2026-07-22) — `isCircularRoute` (+ prioridade de fonte
// e início/fim) vêm agora de `lib/routes-tools/routeRenderModel` (fonte única
// partilhada com TripOverviewMap). Removida a cópia local que divergia.

// Thin out intermediate items to a max count, keeping even spacing
function thinOutMiddle(items, maxMiddle = 6) {
  if (items.length <= 2) return items;
  const middle = items.slice(1, -1);
  if (middle.length <= maxMiddle) return items;
  const step = middle.length / maxMiddle;
  const picked = [];
  for (let i = 0; i < maxMiddle; i++) {
    picked.push(middle[Math.round(i * step)]);
  }
  return [items[0], ...picked, items[items.length - 1]];
}

// Calculate distance between two points using Haversine formula
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Calculate total distance from coordinates array
function calculateTotalDistance(coords) {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += haversineDistance(coords[i - 1][0], coords[i - 1][1], coords[i][0], coords[i][1]);
  }
  return total;
}

// Extract coordinates from GeoJSON
function extractCoordsFromGeoJson(geoJson) {
  const coords = [];
  if (!geoJson || !geoJson.features) return coords;

  for (const feature of geoJson.features) {
    if (feature.geometry) {
      if (feature.geometry.type === "LineString") {
        for (const coord of feature.geometry.coordinates) {
          coords.push([coord[1], coord[0]]); // [lat, lng]
        }
      } else if (feature.geometry.type === "MultiLineString") {
        for (const line of feature.geometry.coordinates) {
          for (const coord of line) {
            coords.push([coord[1], coord[0]]);
          }
        }
      } else if (feature.geometry.type === "Point") {
        coords.push([feature.geometry.coordinates[1], feature.geometry.coordinates[0]]);
      }
    }
  }
  return coords;
}

// b99.9.53 (2026-05-15) — companion to `extractCoordsFromGeoJson` that
// preserves the altitude (3rd entry in GeoJSON 3D coordinates) for the
// elevation panel. Returns `[{lat, lng, alt}]`. `alt` is `null` when the
// source file has no `<ele>` data (GPX usually has some).
function extract3DCoordsFromGeoJson(geoJson) {
  const out = [];
  if (!geoJson || !geoJson.features) return out;
  const pushCoord = (c) => {
    const alt = (typeof c[2] === "number" && Number.isFinite(c[2])) ? c[2] : null;
    out.push({ lat: c[1], lng: c[0], alt });
  };
  for (const feature of geoJson.features) {
    if (!feature.geometry) continue;
    if (feature.geometry.type === "LineString") {
      for (const c of feature.geometry.coordinates) pushCoord(c);
    } else if (feature.geometry.type === "MultiLineString") {
      for (const line of feature.geometry.coordinates) {
        for (const c of line) pushCoord(c);
      }
    } else if (feature.geometry.type === "Point") {
      pushCoord(feature.geometry.coordinates);
    }
  }
  return out;
}


// Component to fit map bounds (includes extra points like POIs)
function FitBounds({ coords, extraCoords = [], fullscreen = false }) {
  const map = useMap();

  useEffect(() => {
    const allCoords = [
      ...(coords || []),
      ...(extraCoords || []),
    ];
    if (allCoords.length > 0) {
      try {
        const bounds = L.latLngBounds(allCoords.map(c => [c.lat || c[0], c.lng || c[1]]));
        if (bounds.isValid()) {
          // b99.9.69 — Asymmetric padding: markers (teardrop/flag) are
          // anchored at the bottom and extend upward ~44px, plus
          // tooltips add ~28px above. Reserve extra TOP headroom so
          // start/end markers near the top edge aren't clipped.
          // Inline maps stay tight horizontally but still get vertical
          // breathing room for the marker icon + label.
          const padTop = fullscreen ? 80 : 56;
          const padBottom = fullscreen ? 50 : 24;
          const padX = fullscreen ? 40 : 16;
          map.fitBounds(bounds, {
            paddingTopLeft: [padX, padTop],
            paddingBottomRight: [padX, padBottom],
            maxZoom: fullscreen ? 14 : 16,
          });
        }
      } catch (error) {
        console.error("Error fitting bounds:", error);
      }
    }
  }, [coords, extraCoords, map, fullscreen]);

  return null;
}

// Component to handle map resize AND re-fit bounds when the container
// size changes (intermediate expand or fullscreen toggle).
//
// b99.9.51 (2026-05-15) — previously only `invalidateSize()` ran, which
// fixed Leaflet's internal viewport calc but left the map centred /
// zoomed on the original 250px bounding box.
//
// b99.9.57 (2026-05-15) — accept an explicit `fitCoords` array so we
// can fit to the AUTHORITATIVE data rather than the layers currently
// painted on the map. Layer-based fitting failed when the polyline /
// markers were still loading at the moment the user toggled fullscreen,
// leaving parts of the route off-screen.
function MapResizeHandler({ expanded, fullscreen, activePanel = null, panelVisible = false, fitCoords = null, recenterTrigger = 0 }) {
  const map = useMap();
  const firstRunRef = useRef(true);

  useEffect(() => {
    // Skip the very first effect run (mount). `FitBounds` does the
    // initial fit; we only want to handle subsequent toggles.
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }
    let cancelled = false;
    const doFit = () => {
      if (cancelled) return;
      map.invalidateSize();
      // b99.9.69 — Asymmetric padding so start/end markers
      // (teardrop/flag, anchor at bottom) + tooltips above don't get
      // clipped when they happen to land near the top edge of bounds.
      const padTop = fullscreen ? 80 : 56;
      const padBottom = fullscreen ? 50 : 24;
      const padX = fullscreen ? 40 : 16;
      const fitOpts = {
        paddingTopLeft: [padX, padTop],
        paddingBottomRight: [padX, padBottom],
        maxZoom: fullscreen ? 14 : 16,
      };
      try {
        // Prefer explicit coords passed by the consumer — most reliable
        // path because it ignores still-loading layers.
        if (Array.isArray(fitCoords) && fitCoords.length > 0) {
          const bounds = L.latLngBounds(
            fitCoords.map((c) =>
              Array.isArray(c) ? [c[0], c[1]] : [c.lat ?? c[0], c.lng ?? c[1]],
            ),
          );
          if (bounds.isValid()) {
            map.fitBounds(bounds, fitOpts);
            return;
          }
        }
        // Fallback — walk the layers currently on the map.
        const bounds = L.latLngBounds([]);
        map.eachLayer((layer) => {
          if (typeof layer.getBounds === "function") {
            const lb = layer.getBounds();
            if (lb && lb.isValid && lb.isValid()) bounds.extend(lb);
          } else if (typeof layer.getLatLng === "function") {
            bounds.extend(layer.getLatLng());
          }
        });
        if (bounds.isValid()) {
          map.fitBounds(bounds, fitOpts);
        }
      } catch (e) {  // noqa
        // Map state can be transient during the height animation —
        // silently no-op; the next user interaction will redraw fine.
      }
    };
    // Two passes: one shortly after the CSS height transition starts
    // (350ms) catches the typical case; a second after 900ms catches
    // the case where the polyline / GeoJSON was still mounting when
    // the first pass ran.
    const t1 = setTimeout(doFit, 350);
    const t2 = setTimeout(doFit, 900);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [expanded, fullscreen, map, fitCoords, recenterTrigger, activePanel, panelVisible]);

  return null;
}

// Map search control using leaflet-geosearch (edit mode only)
function MapSearchControl({ onLocationSelected, searchLabel }) {
  const map = useMap();
  const callbackRef = useRef(onLocationSelected);
  callbackRef.current = onLocationSelected;

  useEffect(() => {
    const provider = new OpenStreetMapProvider({
      params: { "accept-language": "pt", addressdetails: 1 },
    });
    const searchControl = new GeoSearchControl({
      provider,
      style: "bar",
      showMarker: false,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      searchLabel: searchLabel || "Pesquisar localidade...",
      keepResult: false,
    });

    map.addControl(searchControl);

    const handleResult = (result) => {
      if (callbackRef.current && result.location) {
        const loc = result.location;
        const raw = loc.raw || {};
        const address = raw.address || {};
        const name =
          address.village ||
          address.town ||
          address.city ||
          address.municipality ||
          address.county ||
          loc.label.split(",")[0].trim();

        callbackRef.current({
          name,
          lat: loc.y,
          lng: loc.x,
        });
      }
    };

    map.on("geosearch/showlocation", handleResult);

    return () => {
      map.off("geosearch/showlocation", handleResult);
      map.removeControl(searchControl);
    };
  }, [map, searchLabel]);

  return null;
}

// Click handler for route map — adds locality on click with reverse geocoding
function MapClickHandler({ onLocationSelected }) {
  const callbackRef = useRef(onLocationSelected);
  callbackRef.current = onLocationSelected;

  useMapEvents({
    click(e) {
      if (!callbackRef.current) return;
      const { lat, lng } = e.latlng;
      // Reverse geocode with high zoom for precision
      fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { "Accept-Language": "pt" } }
      )
        .then(res => res.json())
        .then(data => {
          const address = data.address || {};
          const name =
            address.neighbourhood ||
            address.suburb ||
            address.hamlet ||
            address.village ||
            address.town ||
            address.city_district ||
            address.city ||
            address.municipality ||
            data.name ||
            `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          callbackRef.current({ name, lat, lng });
        })
        .catch(() => {
          callbackRef.current({ name: `${lat.toFixed(4)}, ${lng.toFixed(4)}`, lat, lng });
        });
    },
  });

  return null;
}

// Component to highlight a point on the map (static ring, no animation)
function HighlightMarker({ point }) {
  if (!point || point.lat == null || point.lng == null) return null;

  return (
    <CircleMarker
      center={[point.lat, point.lng]}
      radius={18}
      pathOptions={{
        color: "#2563eb",
        weight: 3,
        fillColor: "#3b82f6",
        fillOpacity: 0.25,
      }}
    />
  );
}

// Styles for different sources - unified appearance.
// `ROUTE_COLOR` is imported from `./maps/mapIcons` (b99.9.39 — single
// source of truth for pin colours, polyline colours, and POI palette).
// b99.9.32 — polyline styles aligned across all maps. Base routed
// line is `weight: 4, opacity: 0.85` — same in TripOverviewMap and
// the Editor (Ferramentas).
// b99.9.55 (2026-05-15) — centralised polyline styles. Both source
// types (GPX, Google directions) draw the SAME orange route in the
// user-facing maps; per-source colour was a leftover from an
// earlier iteration. Editor de Rotas + Trip Edit + public sheet
// now share the same `POLYLINE_PRIMARY` weight 5.
const STYLES = {
  gpx: { color: ROUTE_COLOR, ...POLYLINE_PRIMARY },
  google: { color: ROUTE_COLOR, ...POLYLINE_PRIMARY },
};

// b99.9.24 — Single source of truth for all route computations. The
// backend's `_compute_directions_or_raise` already chains Google →
// hybrid leg-by-leg → OSRM internally, so the previous client-side
// OSRM fallback (direct hit to `router.project-osrm.org`) is removed
// here. Result: every entry-point in the app (Trip Editor, Routes
// Tools, RouteMapViewer, TripOverviewMap) now renders the SAME
// polyline for the SAME URL/waypoints — that's what the user
// reported as missing 2026-05-15.
// Returns { coords, distance_km, duration_min, source } or null
async function fetchRoute(waypoints, exclude = []) {
  if (!waypoints || waypoints.length < 2) return null;

  // Map OSRM exclude names to Google avoid names
  const avoidMap = { motorway: "highways", toll: "tolls", ferry: "ferries" };
  const googleAvoid = exclude.map(e => avoidMap[e] || e);

  const body = {
    waypoints: waypoints.map((w) => {
      // b99.9.30 — forward `address` (place name) to the backend so
      // Google Routes can do place-aware snapping. This brings the
      // Trip Edit / Trip Viewer routing in line with the Route
      // Editor (Ferramentas), which already sends `address`.
      const out = {
        lat: w.lat,
        lng: w.lng,
        via: !!w.isViaPoint || w.via === true,
      };
      if (w.address) out.address = w.address;
      return out;
    }),
    avoid: googleAvoid,
  };

  // Pick the right endpoint:
  //   - Authenticated user → `/api/routes/directions` (counts toward
  //     the user's daily Google Routes quota — meaningful for
  //     paid-plan billing and abuse prevention).
  //   - Anonymous viewer → `/api/public/directions` (IP-rate-limited;
  //     same routing chain, no per-user quota).
  // Both endpoints share `_compute_directions_or_raise` on the
  // backend so the geometry is identical.
  const hasToken = !!getSessionToken();
  const endpoint = hasToken
    ? `${BACKEND_URL}/api/routes/directions`
    : `${BACKEND_URL}/api/routes/public/directions`;

  try {
    const res = hasToken
      ? await authFetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        })
      : await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
    if (res.ok) {
      const data = await res.json();
      if (data.coords?.length > 0) {
        return {
          coords: data.coords,
          distance_km: data.distance_km,
          duration_min: data.duration_min,
          source: data.source || "google",
        };
      }
    } else if (res.status === 429) {
      // Daily directions quota reached (only happens on the authed
      // endpoint). Surface a clear toast so the user knows why.
      try {
        const err = await res.json();
        toast.error(err.detail || "Limite diário de cálculo de rotas atingido.");
      } catch {
        toast.error("Limite diário de cálculo de rotas atingido.");
      }
    }
  } catch (e) {
    console.warn("Routing backend unavailable:", e.message);
  }

  return null;
}

// Polyline that follows roads via OSRM, falls back to straight lines.
// When `precomputedGeometry` is supplied (persisted server-side per the
// `route_geometry` field on a trip segment), we short-circuit the
// runtime `fetchRoute` — zero Google/OSRM calls on page view, works for
// anonymous users, survives OSRM outages.
function RoutedPolyline({ waypoints, pathOptions, onRouteLoaded, onRoutingStateChange, exclude = [], precomputedGeometry = null, compactArrows = false }) {
  const [routeCoords, setRouteCoords] = useState(null);
  const onRouteLoadedRef = useRef(onRouteLoaded);
  onRouteLoadedRef.current = onRouteLoaded;
  // b99.9.30 — `onRoutingStateChange(true|false)` lets the parent
  // overlay a "A calcular caminho..." pill on top of the map while
  // the Google Routes / OSRM call is in flight. Stable via ref so
  // changing the callback doesn't re-trigger the routing effect.
  const onRoutingStateRef = useRef(onRoutingStateChange);
  onRoutingStateRef.current = onRoutingStateChange;

  const waypointsKey = useMemo(
    () => JSON.stringify(waypoints.map(w => [w.lat, w.lng])),
    [waypoints]
  );
  const excludeKey = useMemo(() => exclude.join(","), [exclude]);

  // Feed the saved server-side geometry directly into state and skip
  // `fetchRoute` entirely. We still fire `onRouteLoaded` so parent
  // components see the authoritative distance/duration.
  useEffect(() => {
    if (precomputedGeometry && Array.isArray(precomputedGeometry.coords) && precomputedGeometry.coords.length > 0) {
      setRouteCoords(precomputedGeometry.coords);
      if (onRouteLoadedRef.current) {
        onRouteLoadedRef.current({
          distance_km: precomputedGeometry.distance_km,
          duration_min: precomputedGeometry.duration_min,
        });
      }
    }
  }, [precomputedGeometry]);

  useEffect(() => {
    // Server-side polyline present → do not touch the network.
    if (precomputedGeometry && Array.isArray(precomputedGeometry.coords) && precomputedGeometry.coords.length > 0) {
      return;
    }
    if (!waypoints || waypoints.length < 2) {
      setRouteCoords(null);
      return;
    }
    let cancelled = false;
    onRoutingStateRef.current?.(true);
    fetchRoute(waypoints, exclude).then(result => {
      if (cancelled) return;
      if (result) {
        setRouteCoords(result.coords);
        if (onRouteLoadedRef.current) {
          onRouteLoadedRef.current({
            distance_km: result.distance_km,
            duration_min: result.duration_min,
          });
        }
      }
      onRoutingStateRef.current?.(false);
    }).catch(() => {
      if (!cancelled) onRoutingStateRef.current?.(false);
    });
    return () => {
      cancelled = true;
      onRoutingStateRef.current?.(false);
    };
  }, [waypointsKey, excludeKey, precomputedGeometry]);

  const positions = routeCoords || waypoints.map(w => [w.lat, w.lng]);
  return (
    <>
      {/* b99.9.55 — white outline halo drawn UNDERNEATH the coloured
          line for contrast on busy basemaps. Centralised style token. */}
      {positions.length >= 2 && (
        <Polyline positions={positions} pathOptions={POLYLINE_OUTLINE} />
      )}
      <Polyline positions={positions} pathOptions={pathOptions} />
      {/* Direction-of-travel chevrons over the snapped polyline.
          Zoom-aware density via the shared component. Color and size
          are intentionally NOT derived from the polyline pathOptions
          — the whole point is high contrast vs. the route line. */}
      <DirectionArrows coords={positions} compact={compactArrows} />
    </>
  );
}

// Category colors for POI markers — imported from ./maps/mapIcons
// (b99.9.39). Removed local declaration to prevent drift.

export function RouteMapViewer({
  gpxUrl,
  googleMapsUrl,
  localities = [],
  pois = [],
  highlightedPoint = null,
  title,
  className = "",
  savedDistance,
  savedDurationMin: savedDurationMinProp,
  onDistanceCalculated,
  onDurationCalculated,
  isEditing = false,
  onAddLocality,
  onAddPoi,
  userCoordinates,
  precomputedGeometry = null,
  tripId = null,
  tripName = null,
  segmentIndex = null,
  elevationCoords = null,
}) {
  const [activeTab, setActiveTab] = useState(null);
  const [gpxData, setGpxData] = useState({ loading: false, error: null, geoJson: null, coords: [], distance: 0 });
  const [googleData, setGoogleData] = useState({ loading: false, error: null, waypoints: [], distance: 0, exclude: [] });
  const [localitiesDistance, setLocalitiesDistance] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // b99.9.100 — increments to trigger MapResizeHandler to re-fit to full route.
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  // b99.9.53 (2026-05-15) — fullscreen layout parity with Editor de Rotas.
  // `mapView` switches between OpenStreetMap (default), Esri World Imagery
  // (satellite) and OpenTopoMap (terrain). `showElevation` toggles the
  // bottom panel — only meaningful when GPX/precomputed coords carry
  // altitude.
  const [mapView, setMapView] = useState("2d");
  // b99.10.07 — Substituído `showElevation` por `activePanel` ("profile"
  // | "histogram" | null) para suportar histograma a par do perfil, em
  // paridade com TripOverviewMap. Mutuamente exclusivos.
  const [activePanel, setActivePanel] = useState("profile");
  const showElevation = activePanel === "profile";
  const showHistogram = activePanel === "histogram";
  const toggleProfile = () => setActivePanel((p) => (p === "profile" ? null : "profile"));
  const toggleHistogram = () => setActivePanel((p) => (p === "histogram" ? null : "histogram"));
  // b99.9.68 — hovered point on the elevation profile. Drives a
  // floating `hoverIcon` marker on the map (same UX as Editor de Rotas).
  const [hoveredCoord, setHoveredCoord] = useState(null);
  // b99.9.55 (2026-05-15) — fullscreen elevation source. For Google /
  // localities-only segments there is no GPX `<ele>` to read locally,
  // but the backend has a public read-only endpoint that returns the
  // precomputed elevation track:
  //   GET /api/trips/{trip_id}/segments/{segment_index}/elevation
  // → `{coordinates:[{lat,lng,alt,t}], has_altitude, source, pending}`
  // This mirrors what the Routes Editor fetches via `POST /api/routes/
  // elevation` for authed users, but with cached server-side results
  // so anonymous viewers don't trigger expensive Open-Meteo calls.
  const [serverElevation, setServerElevation] = useState(null);
  // b99.9.54 (2026-05-15) — local hover state for the fullscreen
  // sidebar's "Pontos" list. Wins over the external `highlightedPoint`
  // prop only while a sidebar row is being hovered; goes back to `null`
  // on mouse-leave so the parent's highlight resumes.
  const [sidebarHover, setSidebarHover] = useState(null);
  const mapRef = useRef(null);

  // b99.10.04 (2026-05-19) — Elevation polling moved to the shared
  // `useTripSegmentElevation` hook so any future change (cooldown,
  // failure UI, retry-after) propagates to both the public trip
  // sheet AND the trip-edit map without divergence.
  const { coordinates: hookElevationCoords, status: elevationStatus, reason: elevationReason } = useTripSegmentElevation({
    tripId,
    segmentIndex,
    skip: Array.isArray(elevationCoords) && elevationCoords.length > 0,
  });
  // Elevation came back empty because the DEM provider was unavailable /
  // rate-limited, or the segment track couldn't be resolved — surface a soft
  // warning so the user knows it's a transient failure to retry, not a route
  // genuinely without altitude. Only "track_empty"/"no_source" (no usable
  // points at all) stay silent.
  const elevationLimited =
    elevationStatus === "failed" &&
    elevationReason !== "track_empty" &&
    elevationReason !== "no_source";
  useEffect(() => {
    if (hookElevationCoords.length > 0) setServerElevation(hookElevationCoords);
  }, [hookElevationCoords]);
  // b99.9.36 — tooltip + arrows toggles. Each pair returns
  // `{visible, toggle}` and feeds a Context Provider wrapping the
  // map children. <MapTooltip> reads the context to flip its
  // `permanent` flag; <DirectionArrows> reads the context to render
  // null when hidden. No DOM-class hacks, hover-to-peek works
  // natively when tooltips are toggled off.
  // b99.9.67 — Default OFF in inline (small map → only start/finish
  // labels visible). A `useEffect` below syncs the toggle to the
  // mode default (ON when maximised).
  // b99.10.15 (2026-05-19) — Removido `alwaysVisible` dos tooltips de
  // origem/destino. Pedido do utilizador: os ÍCONES de origem e destino
  // continuam SEMPRE visíveis (estão renderizados no mapa), mas as
  // TOOLTIPS desses ícones (com o nome da localidade) agora obedecem
  // ao toggle como qualquer outro marker. Paridade com TripOverviewMap.
  const { visible: tooltipsVisible, toggle: toggleTooltips, setVisible: setTooltipsVisible } = useTooltipToggle(false);
  const { visible: arrowsVisible, toggle: toggleArrows } = useArrowsToggle();
  // b99.10.41 (2026-05-21) — Toggle de Início/fim partilhado com o
  // novo `MapVisibilityMenu`. Permite esconder os pinos de start/end
  // (e.g. quando o nome da localidade já está acima do mapa).
  const [showStartEnd, setShowStartEnd] = useState(true);
  const { t } = useTranslation();


  // Close fullscreen on Escape
  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e) => { if (e.key === "Escape") setFullscreen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [fullscreen]);

  // b99.9.67 — Sync the tooltip toggle to the mode default whenever
  // the user enters/exits fullscreen. Inline → OFF (start/finish stay
  // visible via `alwaysVisible`). Fullscreen → ON (all labels).
  useEffect(() => {
    setTooltipsVisible(fullscreen);
  }, [fullscreen, setTooltipsVisible]);

  // Filter localities with valid GPS coordinates
  const localitiesWithCoords = localities.filter(loc => loc.lat && loc.lng);

  // Filter POIs with valid GPS coordinates
  const poisWithCoords = pois.filter(poi => poi.lat != null && poi.lng != null);

  // Callback when OSRM route is loaded for Google Maps tab - update distance with real road distance
  const handleGoogleRouteLoaded = useCallback(({ distance_km, duration_min }) => {
    setGoogleData(prev => ({
      ...prev,
      distance: distance_km,
      duration_min,
    }));
    if (onDistanceCalculated && distance_km > 0) {
      onDistanceCalculated(Math.round(distance_km * 10) / 10, "google");
    }
    if (onDurationCalculated && duration_min > 0) {
      onDurationCalculated(Math.round((duration_min / 60) * 10) / 10, "google");
    }
  }, [onDistanceCalculated, onDurationCalculated]);

  // b99.9.30 — Surface the routing-in-flight state so we can overlay a
  // "A calcular caminho..." pill on top of the map. Counts via ref so
  // multiple concurrent RoutedPolylines (e.g. when the user toggles
  // between gpx/google) don't cancel each other's signals.
  const routingInFlightRef = useRef(0);
  const [isRouting, setIsRouting] = useState(false);
  const handleRoutingStateChange = useCallback((inFlight) => {
    if (inFlight) {
      routingInFlightRef.current += 1;
    } else {
      routingInFlightRef.current = Math.max(0, routingInFlightRef.current - 1);
    }
    setIsRouting(routingInFlightRef.current > 0);
  }, []);

  // Determine available tabs
  const availableTabs = [];
  if (gpxUrl) availableTabs.push("gpx");
  if (googleMapsUrl) availableTabs.push("google");
  // ROUTE-RENDER-MODEL (2026-07-22) — quando não há gpx/gmaps, a fonte de
  // fallback (precomputed > localities > pois) é decidida pela MESMA função
  // partilhada que o TripOverviewMap usa. Elimina a divergência que punha os
  // segmentos 'editor' no tab 'localities' (início/fim nos municípios).
  if (availableTabs.length === 0) {
    const fallbackSource = pickRouteSource({
      precomputedCoordsLen: precomputedGeometry?.coords?.length || 0,
      localitiesLen: localitiesWithCoords.length,
      poisLen: poisWithCoords.length,
    });
    if (fallbackSource) availableTabs.push(fallbackSource);
  }

  // Reset state when source props are cleared (e.g. route type change)
  useEffect(() => {
    if (!gpxUrl) setGpxData({ loading: false, error: null, geoJson: null, coords: [], distance: 0 });
  }, [gpxUrl]);
  useEffect(() => {
    if (!googleMapsUrl) setGoogleData({ loading: false, error: null, waypoints: [], distance: 0, exclude: [] });
  }, [googleMapsUrl]);

  // Reset active tab when available tabs change
  useEffect(() => {
    if (availableTabs.length > 0) {
      if (!activeTab || !availableTabs.includes(activeTab)) {
        setActiveTab(availableTabs[0]);
      }
    } else {
      setActiveTab(null);
    }
  }, [availableTabs.length, gpxUrl, googleMapsUrl, localitiesWithCoords.length, poisWithCoords.length, precomputedGeometry?.coords?.length]);

  // Load GPX
  useEffect(() => {
    if (!gpxUrl) return;

    const loadGpx = async () => {
      setGpxData(prev => ({ ...prev, loading: true, error: null }));
      try {
        const response = await fetch(gpxUrl);
        if (!response.ok) throw new Error("Erro ao carregar GPX");

        const text = await response.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, "text/xml");
        const converted = gpx(xml);

        if (!converted?.features?.length) {
          throw new Error("Nenhum percurso no GPX");
        }

        const coords = extractCoordsFromGeoJson(converted);
        const distance = calculateTotalDistance(coords);

        setGpxData({ loading: false, error: null, geoJson: converted, coords, distance });
        
        if (onDistanceCalculated && distance > 0) {
          onDistanceCalculated(Math.round(distance * 10) / 10, "gpx");
        }
        // Duration: planned GPX exports have no <time> stamps, so the
        // backend can't derive a duration from them and for very long
        // routes OSRM may fail too — leaving the field empty. Estimate
        // it client-side from the distance so every GPX still produces a
        // duration. Recorded GPX (has timestamps) is left to the backend
        // which computes the precise duration from them.
        if (onDurationCalculated && distance > 0 && !/<time>/i.test(text)) {
          const estHours = Math.round((distance / AVG_ROUTE_SPEED_KMH) * 10) / 10;
          if (estHours > 0) onDurationCalculated(estHours, "gpx-estimate");
        }
      } catch (err) {
        setGpxData({ loading: false, error: err.message, geoJson: null, coords: [], distance: 0 });
      }
    };
    loadGpx();
  }, [gpxUrl]);

  // KML loader removed 2026-05-22 — formato deprecado.

  // Parse Google Maps URL
  useEffect(() => {
    if (!googleMapsUrl) return;

    const parseGoogle = async () => {
      setGoogleData((prev) => ({ ...prev, loading: true, error: null }));
      try {
        // b99.9.30 — single source of truth for Google Maps URL parsing.
        // The backend endpoint `/api/public/utils/parse-gmaps-url` is
        // the same one used by the Route Editor (Ferramentas) so the
        // Trip Edit / Trip Viewer flow gets identical waypoints,
        // including:
        //   • `via: true|false` for shaping points (rendered as a
        //     polyline hint, not a labelled stop).
        //   • `address` (the place name from the URL path) so the
        //     downstream routing call can shape the Google Routes
        //     request the same way as the Editor.
        const parsed = await parseGoogleMapsURLViaBackend(googleMapsUrl, api);

        // Build the waypoint shape the rest of the component expects.
        // `isViaPoint` is the legacy flag used by `fetchRoute` to mark
        // shaping points; alias `via` → `isViaPoint` so we don't have
        // to rename everywhere.
        const waypoints = (parsed.waypoints || []).map((wp) => ({
          lat: wp.lat,
          lng: wp.lng,
          name: wp.name || null,
          address: parsed.points?.find(
            (p) => p.lat === wp.lat && p.lng === wp.lng,
          )?.address || null,
          via: wp.via === true,
          isViaPoint: wp.via === true,
        }));

        if (waypoints.length < 2) {
          throw new Error("Não foi possível extrair waypoints do link");
        }

        // Haversine sum for the side-panel — accurate road distance
        // arrives later via `handleGoogleRouteLoaded` from the
        // routing call below.
        let distance = 0;
        for (let i = 1; i < waypoints.length; i++) {
          distance += haversineDistance(
            waypoints[i - 1].lat,
            waypoints[i - 1].lng,
            waypoints[i].lat,
            waypoints[i].lng,
          );
        }

        setGoogleData({
          loading: false,
          error: null,
          waypoints,
          distance,
          exclude: parsed.exclude || [],
        });
      } catch (err) {
        const msg =
          err instanceof RouteParseError
            ? err.message
            : err?.message || "Não foi possível interpretar o link do Google Maps";
        setGoogleData({ loading: false, error: msg, waypoints: [], distance: 0, exclude: [] });
      }
    };
    parseGoogle();
  }, [googleMapsUrl]);

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case "gpx": return {
        ...gpxData,
        distance: (savedDistance > 0) ? savedDistance : gpxData.distance,
      };
      case "google": return {
        ...googleData,
        // Use saved distance when available (backend OSRM is more accurate than frontend with fewer waypoints)
        distance: (savedDistance > 0) ? savedDistance : googleData.distance,
      };
      case "localities": return { loading: false, error: null, waypoints: localitiesWithCoords, distance: (savedDistance > 0) ? savedDistance : localitiesDistance };
      case "precomputed": return { loading: false, error: null, waypoints: localitiesWithCoords, distance: (savedDistance > 0) ? savedDistance : (precomputedGeometry?.distance_km || 0) };
      case "pois_only": return { loading: false, error: null, waypoints: poisWithCoords, distance: 0 };
      default: return { loading: false, error: "Seleciona uma fonte" };
    }
  };

  const currentData = getCurrentData();

  // b99.9.57 — Authoritative coordinates for the fit-on-fullscreen
  // pass in MapResizeHandler. Picks the same source FitBounds uses
  // per active tab so the route is fully visible right after the
  // expand / fullscreen toggle, even if the polyline / GeoJSON is
  // still mounting at the moment the user clicks.
  const fitCoordsForResize = useMemo(() => {
    if (precomputedGeometry?.coords?.length) return precomputedGeometry.coords;
    switch (activeTab) {
      case "gpx": return gpxData?.coords || [];
      case "google": return (googleData?.waypoints || []).map((w) => [w.lat, w.lng]);
      case "localities": return localitiesWithCoords.map((l) => [l.lat, l.lng]);
      case "pois_only": return poisWithCoords.map((p) => [p.lat, p.lng]);
      default: return [];
    }
  }, [activeTab, precomputedGeometry, gpxData?.coords, googleData?.waypoints, localitiesWithCoords, poisWithCoords]);
  // b99.9.53 — duration source for the sidebar: prefer the explicit
  // prop the parent passed (segment.duration_hours), fall back to the
  // precomputed geometry if any.
  const savedDurationMin = (savedDurationMinProp && savedDurationMinProp > 0)
    ? savedDurationMinProp
    : (precomputedGeometry?.duration_min || 0);

  // b99.9.53/55/57 — elevation source for the fullscreen bottom panel.
  // Priority:
  //   1. Prop passed by the parent (SegmentCard already fetches +
  //      polls, so we share its result; eliminates double-fetch).
  //   2. Server-precomputed elevation track fetched here as fallback.
  //   3. GPX `<ele>` parsed locally when active tab is `gpx`.
  // The panel hides itself when every `alt` is null/non-numeric.
  const elevationCoords3D = useMemo(() => {
    if (Array.isArray(elevationCoords) && elevationCoords.length > 0) return elevationCoords;
    if (Array.isArray(serverElevation) && serverElevation.length > 0) return serverElevation;
    if (activeTab === "gpx" && gpxData?.geoJson) return extract3DCoordsFromGeoJson(gpxData.geoJson);
    return [];
  }, [elevationCoords, serverElevation, activeTab, gpxData?.geoJson]);
  const hasElevationData = elevationCoords3D.some((c) => typeof c.alt === "number");

  // b99.9.53 — three-way tile selector for the fullscreen view.
  // b99.10.10 — `TILE_LAYERS` movido para `mapShell/tileLayers.js` e
  // consumido via `<MapTileLayer view={mapView} />`. Mantém-se a paridade
  // OSM / Esri World Imagery / OpenTopoMap.

  if (availableTabs.length === 0 && !isEditing) {
    return null;
  }

  // In edit mode with no data, show empty map for route building
  const showEmptyEditMap = isEditing && availableTabs.length === 0;

  // b99.9.54 — sidebar "Pontos" list with parity to the Routes Editor's
  // SegmentsPanel. The list semantics differ from "Localidades":
  //   • localidades = Nominatim cities the polyline crosses (segment
  //     metadata for SEO / proximity / clustering — not user stops).
  //   • pontos      = the ordered user stops that DEFINE the route.
  //     For a Google-URL segment these are `googleData.waypoints`
  //     (parsed `!1m5!1m1` blocks). For pure-locality segments we
  //     surface the localities AS pontos because they are, de facto,
  //     the only ordered user stops we have. The badge styling and
  //     hover behaviour match `SegmentsPanel.jsx`.
  const pointsList = (() => {
    if (activeTab === "google" && googleData?.waypoints?.length) {
      return googleData.waypoints.map((wp) => ({
        lat: wp.lat,
        lng: wp.lng,
        name: wp.name,
        via: wp.via === true,
      }));
    }
    if (localitiesWithCoords.length > 0) {
      return localitiesWithCoords.map((loc) => ({
        lat: loc.lat,
        lng: loc.lng,
        name: getDisplayName(loc.name) || null,
        via: false,
      }));
    }
    return [];
  })();

  // b99.9.53 — sidebar block reused in the fullscreen layout. Read-only
  // snapshot of the segment's data: title, distance/duration, ordered
  // list of "Pontos" (route stops; styled to mirror the Routes Editor)
  // and PDIs. b99.9.54 swapped the legacy "Localidades" rendering for
  // the parity-styled Pontos list with hover-driven highlight on the map.
  const sidebarContent = !fullscreen ? null : (
    <aside className="w-72 flex-shrink-0 border-r border-border bg-muted/30 overflow-y-auto" data-testid="route-map-fullscreen-sidebar">
      <div className="p-4 space-y-4">
        {title && (
          <div>
            <h3 className="font-semibold text-base leading-tight">{title}</h3>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {currentData.distance > 0 && (
            <div className="rounded-md border border-border bg-card px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Distância</div>
              <div className="font-mono font-semibold text-sm">{currentData.distance.toFixed(1)} km</div>
            </div>
          )}
          {savedDurationMin > 0 && (
            <div className="rounded-md border border-border bg-card px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Duração</div>
              <div className="font-mono font-semibold text-sm">
                {Math.floor(savedDurationMin / 60)}h{String(Math.round(savedDurationMin % 60)).padStart(2, "0")}
              </div>
            </div>
          )}
        </div>

        {pointsList.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Pontos ({pointsList.length})
            </div>
            <ul className="space-y-0.5 border border-border/60 rounded bg-card px-1 py-1" data-testid="route-map-points-list">
              {pointsList.map((p, i) => {
                const label = p.via
                  ? "Ponto de forma"
                  : p.name || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`;
                return (
                  <li
                    key={`pt-${i}`}
                    className={`flex items-center gap-1.5 text-[10.5px] rounded px-1 py-0.5 cursor-pointer ${
                      p.via
                        ? "text-muted-foreground/60 hover:bg-muted/40 italic"
                        : "text-muted-foreground hover:bg-orange-500/10 hover:text-foreground"
                    }`}
                    data-testid={`route-map-point-${i}`}
                    onMouseEnter={() => setSidebarHover({ lat: p.lat, lng: p.lng, name: p.name })}
                    onMouseLeave={() => setSidebarHover(null)}
                    title={label}
                  >
                    {p.via ? (
                      <span
                        aria-hidden
                        className="inline-block h-2 w-2 rounded-full bg-white border border-muted-foreground/60 flex-shrink-0"
                      />
                    ) : (
                      <MapPin className="h-3 w-3 text-orange-500/80 flex-shrink-0" />
                    )}
                    <span className="truncate flex-1">{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {poisWithCoords.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              PDIs ({poisWithCoords.length})
            </div>
            <ul className="space-y-0.5 border border-border/60 rounded bg-card px-1 py-1">
              {poisWithCoords.map((poi, i) => (
                <li
                  key={poi.poi_id || `poi-${i}`}
                  className="flex items-center gap-1.5 text-[10.5px] rounded px-1 py-0.5 cursor-pointer text-muted-foreground hover:bg-amber-500/10 hover:text-foreground"
                  data-testid={`route-map-poi-${i}`}
                  onMouseEnter={() => setSidebarHover({ lat: poi.lat, lng: poi.lng, name: poi.name })}
                  onMouseLeave={() => setSidebarHover(null)}
                  title={poi.name}
                >
                  <Star className="h-3 w-3 text-amber-500 flex-shrink-0" />
                  <span className="truncate flex-1">{poi.name || `PDI ${i + 1}`}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );

  const mapContent = (
    <MapTooltipsContext.Provider value={tooltipsVisible}>
    <MapArrowsContext.Provider value={arrowsVisible}>
    <div
      className={fullscreen
        ? "fixed inset-0 z-[9999] bg-background flex flex-col"
        // b99.10.07 — Inline: flex-col com altura fixa no contentor
        // (em vez de no mapa). Permite que o painel de altitude
        // renderize DENTRO do contentor com `flex-1 min-h-0` no mapa,
        // que assim encolhe para dar espaço. Paridade com TripOverviewMap.
        : `relative flex flex-col rounded-xl overflow-hidden border border-border ${className}`}
      style={fullscreen ? undefined : { height: expanded ? "1000px" : "500px", transition: "height 0.3s ease" }}
      data-testid={fullscreen ? "route-map-viewer-fullscreen" : "route-map-viewer-inline"}
    >
      {/* b99.9.53 — Fullscreen TOP BAR. Houses the operations the user
          asked to surface (title · layer selector · tooltip toggle ·
          arrows toggle · elevation toggle · close). Parity with the
          Routes Editor layout. Hidden in inline mode (controls are
          floating over the map there).
          b99.9.57 — Style alignment with Editor de Rotas: orange
          active state for layer + elevation, inline icon buttons for
          tooltips/arrows/close.
          b99.9.58 — Two-line title: "MAPA DA ETAPA" + `{tripName} | {segmentTitle}`. */}
      {fullscreen && (
        <div className="min-h-12 py-1.5 flex items-center gap-1.5 px-3 border-b border-border bg-card/95 backdrop-blur-sm flex-shrink-0" data-testid="route-map-fullscreen-topbar">
          <div className="flex flex-col leading-tight min-w-0 max-w-[50%]">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              MAPA DA ETAPA
            </span>
            <span className="text-sm font-semibold truncate" title={[tripName, title].filter(Boolean).join(" | ")}>
              {tripName && <span>{tripName}</span>}
              {tripName && title && <span className="text-muted-foreground mx-1.5">|</span>}
              {title && <span className="text-orange-500">{title}</span>}
            </span>
          </div>
          <div className="flex-1" />
          {/* Layer selector — orange active to match Editor.
              b99.10.10 — usa `LayerSelector` partilhado (mapShell). */}
          <LayerSelector
            view={mapView}
            onChange={setMapView}
            testIdPrefix="route-map-layer"
            variant="header"
            hideOnMobile={false}
          />
          {/* b99.10.41 (2026-05-21) — Visibility menu unificado:
              substitui os 2 botões inline (etiquetas + setas) por um
              único trigger Eye com checkboxes para Início/fim,
              Etiquetas e Setas. */}
          <MapVisibilityMenu
            testIdPrefix="route-map-visibility"
            items={[
              { key: "start-end", label: t("map.controls.start_end", "Início e fim"), checked: showStartEnd, onCheckedChange: setShowStartEnd },
              { key: "sep-1", separator: true },
              { key: "labels", label: t("map.controls.labels", "Etiquetas"), checked: tooltipsVisible, onCheckedChange: toggleTooltips },
              { key: "arrows", label: t("map.controls.arrows", "Setas de direção"), checked: arrowsVisible, onCheckedChange: toggleArrows },
            ]}
            variant="icon"
          />
          <button
            type="button"
            onClick={() => setRecenterTrigger((n) => n + 1)}
            className="hover:bg-muted rounded-lg p-2 text-foreground"
            data-testid="route-map-recenter-fullscreen"
            aria-label={t("map.controls.recenter", "Centrar na totalidade da rota")}
            title={t("map.controls.recenter", "Centrar na totalidade da rota")}
          >
            <Locate className="h-4 w-4" />
          </button>
          {hasElevationData && (
            <>
              <button
                type="button"
                onClick={toggleProfile}
                className={`hover:bg-muted rounded-lg p-2 ${showElevation ? "text-orange-500" : "text-foreground"}`}
                data-testid="route-map-elevation-toggle"
                aria-label={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
                title={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
                aria-pressed={showElevation}
              >
                <Mountain className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={toggleHistogram}
                className={`hover:bg-muted rounded-lg p-2 ${showHistogram ? "text-orange-500" : "text-foreground"}`}
                data-testid="route-map-histogram-toggle"
                aria-label={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
                title={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
                aria-pressed={showHistogram}
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="hover:bg-muted rounded-lg p-2"
            data-testid="route-map-fullscreen-close"
            aria-label={t("map.controls.exit_fullscreen", "Sair de ecrã inteiro")}
            title={t("map.controls.exit_fullscreen_title", "Sair de ecrã inteiro (Esc)")}
          >
            <Minimize2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* INLINE TOP BAR — paridade com fullscreen.
          2026-05-26 — pedido directo do utilizador: tirar as
          operações flutuantes (que colidiam com a barra de pesquisa
          do Leaflet em rotas Manuais) e levantá-las para um header
          dedicado com `MAPA DA ETAPA` + `{tripName} | {title}` à
          esquerda e os botões (layer · visibility · centrar ·
          elevação · histograma · expandir) à direita. */}
      {!fullscreen && (
        <div className="min-h-12 py-1.5 flex items-center gap-1.5 px-3 border-b border-border bg-card/95 backdrop-blur-sm flex-shrink-0" data-testid="route-map-inline-topbar">
          <div className="flex flex-col leading-tight min-w-0 max-w-[50%]">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              MAPA DA ETAPA
            </span>
            <span className="text-sm font-semibold truncate" title={[tripName, title].filter(Boolean).join(" | ")}>
              {tripName && <span>{tripName}</span>}
              {tripName && title && <span className="text-muted-foreground mx-1.5">|</span>}
              {title && <span className="text-orange-500">{title}</span>}
            </span>
          </div>
          <div className="flex-1" />
          <LayerSelector
            view={mapView}
            onChange={setMapView}
            testIdPrefix="route-map-layer-inline"
            variant="header"
            hideOnMobile={false}
          />
          <MapVisibilityMenu
            testIdPrefix="route-map-visibility-inline"
            items={[
              { key: "start-end", label: t("map.controls.start_end", "Início e fim"), checked: showStartEnd, onCheckedChange: setShowStartEnd },
              { key: "sep-1", separator: true },
              { key: "labels", label: t("map.controls.labels", "Etiquetas"), checked: tooltipsVisible, onCheckedChange: toggleTooltips },
              { key: "arrows", label: t("map.controls.arrows", "Setas de direção"), checked: arrowsVisible, onCheckedChange: toggleArrows },
            ]}
            variant="icon"
          />
          <button
            type="button"
            onClick={() => setRecenterTrigger((n) => n + 1)}
            className="hover:bg-muted rounded-lg p-2 text-foreground"
            data-testid="route-map-recenter"
            aria-label={t("map.controls.recenter", "Centrar na totalidade da rota")}
            title={t("map.controls.recenter", "Centrar na totalidade da rota")}
          >
            <Locate className="h-4 w-4" />
          </button>
          {hasElevationData && (
            <>
              <button
                type="button"
                onClick={toggleProfile}
                className={`hover:bg-muted rounded-lg p-2 ${showElevation ? "text-orange-500" : "text-foreground"}`}
                data-testid="route-map-elevation-toggle-inline"
                aria-label={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
                title={showElevation ? t("map.controls.hide_profile", "Esconder perfil de altitude") : t("map.controls.show_profile", "Mostrar perfil de altitude")}
                aria-pressed={showElevation}
              >
                <Mountain className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={toggleHistogram}
                className={`hover:bg-muted rounded-lg p-2 ${showHistogram ? "text-orange-500" : "text-foreground"}`}
                data-testid="route-map-histogram-toggle-inline"
                aria-label={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
                title={showHistogram ? t("map.controls.hide_histogram", "Esconder histograma de altitude") : t("map.controls.show_histogram", "Mostrar histograma de altitude")}
                aria-pressed={showHistogram}
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="hover:bg-muted rounded-lg p-2 text-foreground"
            data-testid="route-map-fullscreen-toggle-inline"
            aria-label={t("map.controls.open_fullscreen", "Ampliar mapa")}
            title={t("map.controls.open_fullscreen", "Ampliar mapa")}
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* MAIN AREA — sidebar (fullscreen only) + map column. The map
          column always provides a `relative` ancestor so the floating
          tabs / title / inline controls position correctly in both
          modes. b99.10.07 — fullscreen column é agora flex-col para
          os painéis de altitude (flex-shrink-0) e o mapa (flex-1)
          dividirem a altura — paridade com TripOverviewMap. */}
      <div className={fullscreen ? "flex flex-1 min-h-0 overflow-hidden" : "flex flex-1 min-h-0"}>
        {sidebarContent}
        <div className={fullscreen ? "relative flex-1 min-h-0 overflow-hidden flex flex-col" : "relative flex-1 min-h-0 flex flex-col"}>
      {/* Tabs */}
      {availableTabs.length > 1 && (
        <div className="absolute top-2 left-14 z-[1000]">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-background/90 backdrop-blur-sm">
              {availableTabs.includes("gpx") && (
                <TabsTrigger value="gpx" className="text-xs data-[state=active]:bg-green-500 data-[state=active]:text-white">
                  GPX
                </TabsTrigger>
              )}
              {availableTabs.includes("google") && (
                <TabsTrigger value="google" className="text-xs data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                  Google
                </TabsTrigger>
              )}
              {availableTabs.includes("localities") && (
                <TabsTrigger value="localities" className="text-xs data-[state=active]:bg-violet-500 data-[state=active]:text-white">
                  Localidades
                </TabsTrigger>
              )}
              {availableTabs.includes("pois_only") && (
                <TabsTrigger value="pois_only" className="text-xs data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  {t('trip_form.pois_card.acronym_plural')}
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Title flutuante removido: já consta no header inline acima
          (`route-map-inline-topbar`). 2026-05-26. */}

      {/* Toolbar flutuante removido: substituído pelo `route-map-inline-topbar`
          acima do mapa (paridade com fullscreen). 2026-05-26. */}

      {/* Bottom badges — INLINE mode only. Fullscreen surfaces these
          stats in the sidebar instead. */}
      {!fullscreen && (currentData.distance > 0 || poisWithCoords.length > 0) && (
        <div className="absolute bottom-2 left-2 z-[1000] flex items-stretch gap-1.5">
          {currentData.distance > 0 && (
            <div className="bg-background/90 backdrop-blur-sm px-3 rounded-lg border border-border flex items-center">
              <span className="text-sm font-mono font-bold">{currentData.distance.toFixed(1)} km</span>
            </div>
          )}
          {poisWithCoords.length > 0 && (
            <div className="bg-background/90 backdrop-blur-sm px-3 rounded-lg border border-border flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-sm font-medium">{poisWithCoords.length} {poisWithCoords.length !== 1 ? t('trip_form.pois_card.acronym_plural') : t('trip_form.pois_card.acronym')}</span>
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {!showEmptyEditMap && currentData.loading && (
        <div className={`flex items-center justify-center bg-muted/50 flex-1 min-h-0`}>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error state */}
      {!showEmptyEditMap && !currentData.loading && currentData.error && (
        <div className={`flex items-center justify-center bg-muted/50 flex-1 min-h-0`}>
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>{currentData.error}</span>
          </div>
        </div>
      )}

      {/* Map */}
      {(showEmptyEditMap || (!currentData.loading && !currentData.error)) && (
        <div
          className="relative flex-1 min-h-0"
        >
        {/* Floating "A calcular caminho..." pill — shown while
            RoutedPolyline has an in-flight Google Routes / OSRM
            request. Works on both the inline map and the fullscreen
            portal (same wrapper). b99.9.30 (2026-05-15). */}
        {isRouting && (
          <div
            className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none"
            data-testid="route-viewer-routing-pill"
          >
            <div className="flex items-center gap-2 bg-background/95 backdrop-blur-sm border border-border rounded-full shadow-lg px-3 py-1.5 text-xs font-medium">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span>{t("trip_form.route_card.calculating_route", "A calcular caminho…")}</span>
            </div>
          </div>
        )}
        {/* Altitude unavailable due to an external DEM limit (Open-Meteo
            daily cap / fallback down). Tells the user it's transient and
            to retry later, instead of silently showing no profile. */}
        {elevationLimited && !isRouting && (
          <div
            className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none max-w-[90%]"
            data-testid="route-viewer-elevation-limited-pill"
          >
            <div className="flex items-center gap-2 bg-amber-500/95 text-amber-950 backdrop-blur-sm border border-amber-600 rounded-full shadow-lg px-3 py-1.5 text-xs font-medium">
              <Mountain className="h-3.5 w-3.5 shrink-0" />
              <span>{t("trip_form.route_card.elevation_limited", "Serviço de altitude temporariamente no limite — tenta Reprocessar mais tarde.")}</span>
            </div>
          </div>
        )}
        {/* Tooltip toggle was moved next to the fullscreen button
            (top-right) in b99.9.33 for layout parity with the other
            maps. */}
        <MapContainer
          ref={mapRef}
          center={userCoordinates ? [userCoordinates.lat, userCoordinates.lng] : [39.5, -8.0]}
          zoom={userCoordinates ? 10 : 7}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
          zoomControl={!fullscreen}
          zoomAnimation={false}
          fadeAnimation={false}
        >
          {/* b99.9.56 (2026-05-15) — Zoom +/- on the RIGHT in fullscreen,
              matching the Routes Editor layout. Inline mode keeps the
              default top-left position so it doesn't collide with our
              floating top-right button cluster (tooltip · arrows ·
              expand · fullscreen). */}
          {fullscreen && <ZoomControl position="topright" />}
          <MapTileLayer view={mapView} />
          <MapResizeHandler expanded={expanded} fullscreen={fullscreen} activePanel={activePanel} panelVisible={hasElevationData && activePanel !== null} fitCoords={fitCoordsForResize} recenterTrigger={recenterTrigger} />

          {/* When async elevation data arrives, the altitude panel mounts
              and the map (flex-1) shrinks. `MapResizeHandler` re-runs on
              the `panelVisible` change above to invalidateSize + re-fit so
              the polyline recenters instead of staying off-centre. */}

          {/* Search + click controls (edit mode only) */}
          {isEditing && onAddLocality && (
            <>
              <MapSearchControl
                onLocationSelected={onAddLocality}
                searchLabel={t("trip_form.route_card.map_search_placeholder", "Pesquisar ponto...")}
              />
              <MapClickHandler onLocationSelected={onAddLocality} />
            </>
          )}

          {/* GPX Layer */}
          {activeTab === "gpx" && gpxData.geoJson && (
            <>
              <GeoJSON
                key="gpx"
                data={gpxData.geoJson}
                style={STYLES.gpx}
                pointToLayer={() => L.circleMarker([0, 0], { radius: 0, opacity: 0, fillOpacity: 0 })}
              />
              {/* Direction-of-travel chevrons over the GPX track.
                  Same zoom-aware shared component used by the Editor
                  and the Google Routes overlay. */}
              {gpxData.coords?.length > 1 && (
                <DirectionArrows coords={gpxData.coords} compact={!fullscreen} />
              )}
              {/* Start/End markers from actual track coordinates */}
              {gpxData.coords?.length > 1 && showStartEnd && (() => {
                const startCoord = gpxData.coords[0];
                const endCoord = gpxData.coords[gpxData.coords.length - 1];
                const circular = isCircularRoute(gpxData.coords);
                const startName = getDisplayName(localitiesWithCoords[0]?.name);
                const endName = localitiesWithCoords.length > 1 ? getDisplayName(localitiesWithCoords[localitiesWithCoords.length - 1]?.name) : null;
                if (circular) {
                  return (
                    <Marker position={[startCoord[0], startCoord[1]]} icon={circularIcon}>
                      <MapTooltip direction="top" offset={[0, -40]} opacity={0.95} className="map-tooltip-compact">
                        <strong>{startName || endName || t("trip_form.route_card.start_end_point", "Partida / Chegada")}</strong>

                      </MapTooltip>
                    </Marker>
                  );
                }
                return (
                  <>
                    <Marker position={[startCoord[0], startCoord[1]]} icon={startIcon}>
                      {startName && <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact"><strong>{startName}</strong></MapTooltip>}
                    </Marker>
                    <Marker position={[endCoord[0], endCoord[1]]} icon={finishIcon}>
                      {endName && <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact"><strong>{endName}</strong></MapTooltip>}
                    </Marker>
                  </>
                );
              })()}
              <FitBounds coords={gpxData.coords} extraCoords={poisWithCoords} fullscreen={fullscreen} />
            </>
          )}

          {/* KML Layer removed 2026-05-22 — formato deprecado. */}

          {/* Google Maps Layer */}
          {activeTab === "google" && googleData.waypoints.length > 0 && (
            <>
              <RoutedPolyline
                waypoints={googleData.waypoints}
                pathOptions={STYLES.google}
                onRouteLoaded={handleGoogleRouteLoaded}
                onRoutingStateChange={handleRoutingStateChange}
                exclude={googleData.exclude}
                precomputedGeometry={precomputedGeometry}
                compactArrows={!fullscreen}
              />
              {/* Via-shaping points (b99.9.41) — rendered identically
                  to the Editor de Rotas via the shared
                  <ViaShapingDots> component. Without this, the small
                  white dots that mark Google Maps `!3m4!1m2` shaping
                  hints would only appear in the Editor, NOT in Trip
                  Edit, even though both extract the same waypoints. */}
              {/* b99.9.68 — Hide the white via-shaping dots in the
                  inline (minimized) public map. These are auxiliary
                  marks that exist only to force specific roads on the
                  routing engine; the user doesn't need to see them
                  when the map is small. They remain visible in
                  fullscreen so power users can still verify the
                  routing hints. */}
              {fullscreen && <ViaShapingDots waypoints={googleData.waypoints} color={ROUTE_COLOR} />}
              {(() => {
                const mainWps = googleData.waypoints.filter(wp => !wp.isViaPoint);
                const isCircularGmaps = mainWps.length > 1 &&
                  haversineDistance(mainWps[0].lat, mainWps[0].lng,
                    mainWps[mainWps.length - 1].lat, mainWps[mainWps.length - 1].lng) < 0.15;
                return mainWps.map((wp, i, arr) => {
                  const isFirst = i === 0;
                  const isLast = i === arr.length - 1;
                  if (isCircularGmaps && isFirst) {
                    if (!showStartEnd) return null;
                    return (
                      <Marker key={i} position={[wp.lat, wp.lng]} icon={circularIcon}>
                        <MapTooltip direction="top" offset={[0, -40]} opacity={0.95} className="map-tooltip-compact">
                          <strong>{getDisplayName(wp.name) || t("trip_form.route_card.start_end_point", "Partida / Chegada")}</strong>

                        </MapTooltip>
                      </Marker>
                    );
                  }
                  if (isCircularGmaps && isLast) return null;
                  if (isFirst || isLast) {
                    if (!showStartEnd) return null;
                    return (
                      <Marker key={i} position={[wp.lat, wp.lng]} icon={isFirst ? startIcon : finishIcon}>
                        <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact">
                          <strong>{getDisplayName(wp.name) || `Ponto ${i + 1}`}</strong>
                        </MapTooltip>
                      </Marker>
                    );
                  }
                  return (
                    <Marker key={i} position={[wp.lat, wp.lng]} icon={fullscreen ? segmentIcon(ROUTE_COLOR) : dotIcon(ROUTE_COLOR)}>
                      <MapTooltip direction="top" offset={fullscreen ? [0, -24] : [0, -10]} opacity={0.95} className="map-tooltip-compact">
                        <strong>{getDisplayName(wp.name) || `Ponto ${i + 1}`}</strong>
                      </MapTooltip>
                    </Marker>
                  );
                });
              })()}
              <FitBounds coords={googleData.waypoints} extraCoords={poisWithCoords} fullscreen={fullscreen} />
            </>
          )}

          {/* Precomputed (Editor / My Routes) Layer — polyline densa + marcadores
              início/fim nos EXTREMOS REAIS da geometria (não nos municípios). */}
          {activeTab === "precomputed" && (precomputedGeometry?.coords?.length || 0) > 1 && (
            <>
              <RoutedPolyline
                waypoints={localitiesWithCoords}
                pathOptions={{ color: ROUTE_COLOR, weight: 4, opacity: 0.85 }}
                precomputedGeometry={precomputedGeometry}
                onRoutingStateChange={handleRoutingStateChange}
                compactArrows={!fullscreen}
              />
              {(() => {
                const coords = precomputedGeometry.coords;
                // Paragens: preferir os waypoints NATIVOS do Editor (kind/role,
                // pontos de estrada marcados via) quando existirem; senão, as
                // localidades geocodificadas. Fonte única partilhada.
                const nativeStops = nativeStopsFromGeometry(precomputedGeometry);
                const stopsSource = nativeStops.length > 0
                  ? nativeStops.filter((s) => !s.via)
                  : localitiesWithCoords;
                const startName = getDisplayName(stopsSource[0]?.name);
                const endName = stopsSource.length > 1
                  ? getDisplayName(stopsSource[stopsSource.length - 1]?.name)
                  : null;
                // Início/fim SEMPRE dos extremos reais da geometria (fonte única).
                const { start, end, isCircular: circular } = startEndFromCoords(coords, { startName, endName });
                // Paragens intermédias afinadas, SEM os extremos (para não
                // duplicar os marcadores porta/bandeira).
                const middle = thinOutMiddle(stopsSource, 6).slice(1, -1);
                return (
                  <>
                    {middle.map((loc, i) => (
                      <Marker
                        key={loc.locality_id || `mid-${i}`}
                        position={[loc.lat, loc.lng]}
                        icon={fullscreen ? segmentIcon(ROUTE_COLOR) : dotIcon(ROUTE_COLOR)}
                      >
                        <MapTooltip direction="top" offset={fullscreen ? [0, -24] : [0, -10]} opacity={0.95} className="map-tooltip-compact">
                          <strong>{getDisplayName(loc.name) || `Ponto ${i + 2}`}</strong>
                        </MapTooltip>
                      </Marker>
                    ))}
                    {showStartEnd && circular && start && (
                      <Marker position={[start.lat, start.lng]} icon={circularIcon}>
                        <MapTooltip direction="top" offset={[0, -40]} opacity={0.95} className="map-tooltip-compact">
                          <strong>{startName || endName || t("trip_form.route_card.start_end_point", "Partida / Chegada")}</strong>
                        </MapTooltip>
                      </Marker>
                    )}
                    {showStartEnd && !circular && start && end && (
                      <>
                        <Marker position={[start.lat, start.lng]} icon={startIcon}>
                          {startName && <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact"><strong>{startName}</strong></MapTooltip>}
                        </Marker>
                        <Marker position={[end.lat, end.lng]} icon={finishIcon}>
                          {endName && <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact"><strong>{endName}</strong></MapTooltip>}
                        </Marker>
                      </>
                    )}
                  </>
                );
              })()}
              <FitBounds coords={precomputedGeometry.coords} extraCoords={poisWithCoords} fullscreen={fullscreen} />
            </>
          )}

          {/* Localities Layer (manual waypoints with GPS) */}
          {activeTab === "localities" && localitiesWithCoords.length > 0 && (
            <>
              {(localitiesWithCoords.length > 1 || (precomputedGeometry?.coords?.length || 0) > 1) && (
                <RoutedPolyline
                  waypoints={localitiesWithCoords}
                  pathOptions={{ color: ROUTE_COLOR, weight: 4, opacity: 0.85 }}
                  precomputedGeometry={precomputedGeometry}
                  onRoutingStateChange={handleRoutingStateChange}
                  compactArrows={!fullscreen}
                  onRouteLoaded={(data) => {
                    if (data.distance_km > 0) {
                      setLocalitiesDistance(Math.round(data.distance_km * 10) / 10);
                      if (onDistanceCalculated) {
                        onDistanceCalculated(Math.round(data.distance_km * 10) / 10, "localities");
                      }
                    }
                    if (data.duration_min > 0 && onDurationCalculated) {
                      onDurationCalculated(Math.round((data.duration_min / 60) * 10) / 10, "localities");
                    }
                  }}
                />
              )}
              {(() => {
                const visibleLocs = thinOutMiddle(localitiesWithCoords, 6);
                const isCircular = localitiesWithCoords.length > 2 &&
                  haversineDistance(localitiesWithCoords[0].lat, localitiesWithCoords[0].lng,
                    localitiesWithCoords[localitiesWithCoords.length - 1].lat, localitiesWithCoords[localitiesWithCoords.length - 1].lng) < 0.15;
                return visibleLocs.map((loc, i) => {
                  const isFirst = i === 0;
                  const isLast = i === visibleLocs.length - 1;
                  if (isFirst && isLast) {
                    if (!showStartEnd) return null;
                    return (
                      <Marker key={loc.locality_id || i} position={[loc.lat, loc.lng]} icon={startIcon}>
                        <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact">
                          <strong>{getDisplayName(loc.name) || `Ponto ${i + 1}`}</strong>
                        </MapTooltip>
                      </Marker>
                    );
                  }
                  if (isCircular && isFirst) {
                    if (!showStartEnd) return null;
                    return (
                      <Marker key={loc.locality_id || i} position={[loc.lat, loc.lng]} icon={circularIcon}>
                        <MapTooltip direction="top" offset={[0, -40]} opacity={0.95} className="map-tooltip-compact">
                          <strong>{getDisplayName(loc.name) || t("trip_form.route_card.start_end_point", "Partida / Chegada")}</strong>

                        </MapTooltip>
                      </Marker>
                    );
                  }
                  if (isCircular && isLast) return null;
                  if (isFirst || isLast) {
                    if (!showStartEnd) return null;
                    return (
                      <Marker key={loc.locality_id || i} position={[loc.lat, loc.lng]} icon={isFirst ? startIcon : finishIcon}>
                        <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact">
                          <strong>{getDisplayName(loc.name) || `Ponto ${i + 1}`}</strong>
                        </MapTooltip>
                      </Marker>
                    );
                  }
                  return (
                    <Marker key={loc.locality_id || i} position={[loc.lat, loc.lng]} icon={fullscreen ? segmentIcon(ROUTE_COLOR) : dotIcon(ROUTE_COLOR)}>
                      <MapTooltip direction="top" offset={fullscreen ? [0, -24] : [0, -10]} opacity={0.95} className="map-tooltip-compact">
                        <strong>{getDisplayName(loc.name) || `Ponto ${i + 1}`}</strong>
                      </MapTooltip>
                    </Marker>
                  );
                });
              })()}
              <FitBounds coords={(precomputedGeometry?.coords?.length || 0) > 1 ? precomputedGeometry.coords : localitiesWithCoords} extraCoords={poisWithCoords} fullscreen={fullscreen} />
            </>
          )}

          {/* POI Markers - shown on all tabs as overlay (pin-shaped icons) */}
          {poisWithCoords.length > 0 && poisWithCoords.map((poi, i) => (
            <Marker
              key={poi.poi_id || `poi-${i}`}
              position={[poi.lat, poi.lng]}
              icon={poiIcon(poi.category)}
            >
              <MapTooltip direction="top" offset={[0, -28]} opacity={0.95} className="map-tooltip-compact">
                <strong>{poi.name}</strong>
              </MapTooltip>
            </Marker>
          ))}

          {/* FitBounds for POIs-only view */}
          {activeTab === "pois_only" && poisWithCoords.length > 0 && (
            <FitBounds coords={poisWithCoords} fullscreen={fullscreen} />
          )}

          {/* b99.9.55 — Hover highlight ring. When the global map
              tooltips are ON, the hovered marker already shows a
              permanent tooltip; we suppress the hover tooltip to avoid
              the duplicate-tooltip artefact the user reported. When
              tooltips are OFF, we surface the point's name so the user
              can still identify what they're hovering. Uses the
              centralised `HOVER_RING` style — change once, applies
              everywhere. */}
          {(sidebarHover || highlightedPoint) && (
            <HoverHighlight
              point={sidebarHover || highlightedPoint}
              label={(sidebarHover || highlightedPoint)?.name || null}
              showTooltip={!tooltipsVisible}
            />
          )}

          {/* b99.9.68 — Floating hover marker driven by the elevation
              profile (`ElevationProfileWeb.onHoverPoint`). Same UX as
              the Editor de Rotas: as the user scrubs the chart the
              corresponding location on the map highlights. */}
          {hoveredCoord && (
            <Marker
              position={[hoveredCoord.lat, hoveredCoord.lng]}
              icon={hoverIcon}
              interactive={false}
            />
          )}
        </MapContainer>
        </div>
      )}

      {/* b99.10.07 — Painéis de altitude partilhados com TripOverviewMap.
          Perfil XOR histograma. Renderizam DENTRO do contentor flex-col,
          encolhendo o mapa (flex-1) para acomodar o painel (flex-shrink-0).
          Em fullscreen ficam dentro da coluna do mapa, deixando a sidebar
          a ocupar toda a altura disponível. Paridade total com o mapa
          público. */}
      {hasElevationData && showElevation && (
        <TripOverviewElevationPanel
          coordinates={elevationCoords3D}
          height={fullscreen ? 140 : 120}
          onHoverPoint={setHoveredCoord}
          onClose={() => setActivePanel(null)}
          fullscreen={fullscreen}
        />
      )}
      {hasElevationData && showHistogram && (
        <TripOverviewHistogramPanel
          coordinates={elevationCoords3D}
          height={fullscreen ? 140 : 120}
          onClose={() => setActivePanel(null)}
          fullscreen={fullscreen}
        />
      )}
        </div>{/* /map column */}
      </div>{/* /main area */}
    </div>
    </MapArrowsContext.Provider>
    </MapTooltipsContext.Provider>
  );

  return fullscreen ? createPortal(mapContent, document.body) : mapContent;
}

export default RouteMapViewer;
